<template>
  <block-item-card
    :min-height="150"
    :title="assumptionsTitle"
    :title-icon="crisisIconColor"
  >
    <v-row
      no-gutters
      class="ma-5 pt-1 mt-4 d-flex justify-space-between"
    >
      <div>
        <asset-button-group
          :height="40"
          :items="tabSelectorItems"
          :selected.sync="selectedTab"
          disable-dropdown-on-mobile
        />
      </div>
      <div class="d-flex align-center flex-row">
        <banking-switch
          v-model="editMode"
          :disabled="selectedTab==='BankReadiness' || !tableView"
          :disabled-label="$t('RiskLiquidity.Section.DefenceReadiness.MildCrisisAssumptions.ModeSelector.Default')"
          :enabled-label="$t('RiskLiquidity.Section.DefenceReadiness.MildCrisisAssumptions.ModeSelector.Edit')"
          class="mr-10"
        />
        <banking-switch
          v-model="tableView"
          :disabled="selectedTab==='BankReadiness'"
          :disabled-label="$t('RiskLiquidity.Section.DefenceReadiness.MildCrisisAssumptions.ViewSelector.Modular')"
          :enabled-label="$t('RiskLiquidity.Section.DefenceReadiness.MildCrisisAssumptions.ViewSelector.Table')"
          class="mr-10"
        />
      </div>
    </v-row>
    <template v-if="selectedTab === 'DepositorBehavior'">
      <v-row
        v-if="tableView"
        class="ma-0 mt-1"
      >
        <depositor-behavior-table-view
          :edit-mode="editMode"
          :table-data="tableBaseItems"
        />
      </v-row>
      <v-row v-if="!tableView">
        <v-col>
          <depositor-behavior-modular-view
            :edit-mode="editMode"
            :table-data="tableBaseItems"
          />
        </v-col>
      </v-row>
    </template>
    <v-row v-if="selectedTab === 'BankReadiness'">
      <v-col style="overflow-x: auto;">
        <bank-readiness-modular-view
          :table-data="tableBaseItems"
        />
      </v-col>
    </v-row>
  </block-item-card>
</template>

    <script>
import BlockItemCard from '@/theme/default/components/common/BlockItemCard'
import AssetButtonGroup from '@/theme/default/components/common/AssetButtonGroup'
import BankingSwitch from '@/theme/default/components/common/BankingSwitch'
import { toDecimalSignificant, toNumberFormatNotationShort } from '@/lib/utils/helper'
import riskBaseCurrency from '@/theme/default/views/risks/mixins/riskBaseCurrency'
import DepositorBehaviorTableView from '@/theme/default/views/banking/risk/liquidity/DefenceReadiness/blocks/DepositorBehaviorTableView.vue'
import DepositorBehaviorModularView from '@/theme/default/views/banking/risk/liquidity/DefenceReadiness/blocks/DepositorBehaviorModularView.vue'
import BankReadinessModularView from '@/theme/default/views/banking/risk/liquidity/DefenceReadiness/blocks/BankReadinessModularView.vue'

export default {
  name      : 'MildCrisisAssumptions',
  components: {
    BlockItemCard,
    AssetButtonGroup,
    BankingSwitch,
    DepositorBehaviorTableView,
    DepositorBehaviorModularView,
    BankReadinessModularView
  },
  mixins   : [riskBaseCurrency],
  dataStore: {
    SelectedScenario    : 'RiskLiquidity.CrisisScenario.SelectedScenario',
    BankReadiness       : 'RiskLiquidity.BankReadiness.Data',
    riskBaseCurrencyData: 'Risks.BaseCurrency.Data',
    DepositBehavior     : 'RiskLiquidity.DepositBehavior.Data',
    AssumptionData      : 'RiskLiquidity.DepositBehavior.Assumptions'
  },
  data () {
    return {
      selectedTab: 'BankReadiness',
      tableView  : false,
      editMode   : false
    }
  },
  computed: {
    id () {
      return this.user?.selectedAccountId || null
    },
    selectedScenario () {
      return this.SelectedScenario
    },
    crisisIconColor () {
      return this.$t(`RiskLiquidity.Section.DefenceReadiness.CrisisScenarios.Scenarios.${this.selectedScenario}.Color`)
    },
    assumptionsTitle () {
      return !this.selectedScenario
        ? this.$t('RiskLiquidity.Section.DefenceReadiness.MildCrisisAssumptions.EditedScenarioTitle')
        : this.$t(`RiskLiquidity.Section.DefenceReadiness.MildCrisisAssumptions.CrisisScenario.${this.selectedScenario}`)
    },
    tabSelectorItems () {
      return [
        { Title: this.$t('RiskLiquidity.Section.DefenceReadiness.MildCrisisAssumptions.TabSelector.BankReadiness.Title'), Value: 'BankReadiness', Svg: this.$t('RiskLiquidity.Section.DefenceReadiness.MildCrisisAssumptions.TabSelector.BankReadiness.Svg') },
        { Title: this.$t('RiskLiquidity.Section.DefenceReadiness.MildCrisisAssumptions.TabSelector.DepositorBehavior.Title'), Value: 'DepositorBehavior', Svg: this.$t('RiskLiquidity.Section.DefenceReadiness.MildCrisisAssumptions.TabSelector.DepositorBehavior.Svg') }
      ]
    },
    viewSelectorItems () {
      return [
        { Title: this.$t('RiskLiquidity.Section.DefenceReadiness.MildCrisisAssumptions.ViewSelector.Modular'), Value: 'Modular' },
        { Title: this.$t('RiskLiquidity.Section.DefenceReadiness.MildCrisisAssumptions.ViewSelector.Table'), Value: 'Table' }
      ]
    },
    modeSelectorItems () {
      return [
        { Title: this.$t('RiskLiquidity.Section.DefenceReadiness.MildCrisisAssumptions.ModeSelector.Default'), Value: 'Default' },
        { Title: this.$t('RiskLiquidity.Section.DefenceReadiness.MildCrisisAssumptions.ModeSelector.Custom'), Value: 'Custom' }
      ]
    },
    tableBaseItems () {
      if (!this.DepositBehavior.items || this.DepositBehavior.items.length === 0) {
        return {}
      }
      const timeDepositsInsured = this.createTableRow(this.DepositBehavior.items, 'timeDepositsInsured')
      const timeDepositsUninsured = this.createTableRow(this.DepositBehavior.items, 'timeDepositsUninsured')
      const savingsDepositsInsured = this.createTableRow(this.DepositBehavior.items, 'savingsDepositsInsured')
      const savingsDepositsUninsured = this.createTableRow(this.DepositBehavior.items, 'savingsDepositsUninsured')
      const demandDepositsInsured = this.createTableRow(this.DepositBehavior.items, 'demandDepositsInsured')
      const demandDepositsUninsured = this.createTableRow(this.DepositBehavior.items, 'demandDepositsUninsured')

      const totalOutflow = {}
      this.DepositBehavior.items.map((item) => {
        totalOutflow[`d${item.day}`] = item.totalOutflow
      })
      return {
        timeDepositsInsured     : timeDepositsInsured,
        timeDepositsUninsured   : timeDepositsUninsured,
        savingsDepositsInsured  : savingsDepositsInsured,
        savingsDepositsUninsured: savingsDepositsUninsured,
        demandDepositsInsured   : demandDepositsInsured,
        demandDepositsUninsured : demandDepositsUninsured,
        totalOutflow            : totalOutflow
      }
    }

  },
  watch: {
    selectedTab: {
      handler (newVal) {
        this.tableView = false
        this.editMode = false
      }
    },
    tableView: {
      handler (newVal) {
        this.editMode = false
      }
    },
    editMode: {
      handler (newVal) {
        this.$bus.$emit('RiskLiquidity:DefenceReadiness:EditModeChanged', newVal)
      }
    }
  },
  created () {
    this.$bus.$on('RiskLiquidity:DefenceReadiness:SetEditMode', (newVal) => {
      this.editMode = newVal
    })
    this.$bus.$on('RiskLiquidity:CrisisScenario:Changed', this.onCrisisScenarioChanged)
  },
  beforeDestroy () {
    this.$bus.$off('RiskLiquidity:DefenceReadiness:SetEditMode')
    this.$bus.$off('RiskLiquidity:CrisisScenario:Changed', this.onCrisisScenarioChanged)
  },

  mounted () {},
  methods: {
    formatNumber (value) {
      return toDecimalSignificant(value, 2)
    },
    createTableRow (items, key) {
      const tableRow = { percentage: {}, amount: {} }
      items.map((item) => {
        tableRow.percentage[`d${item.day}`] = `${item[key].percentage.toFixed(2)}%`
        tableRow.amount[`d${item.day}`] = toNumberFormatNotationShort(item[key].amount) || '$0'
      })
      return tableRow
    },
    onCrisisScenarioChanged (scenario) {
      // When crisis scenario changes, turn off edit mode to lose edited results
      if (this.editMode) {
        this.editMode = false
      }
    }
  }
}
    </script>

    <style scoped>

    </style>
